import { UserDevice } from '@mews-commander/data-access';
import { LocalizedLongDateFromUtc, LocalizedShortTimeFromUtc } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Container, Stack, Tag, Typography } from '@optimus-web/core';
import { AuthorizedDeviceRemoveDialog } from './AuthorizedDeviceRemoveDialog';

interface AuthorizedDeviceProps {
    device: UserDevice;
    currentDeviceId?: string | null;
    onUserRemove: (id: string) => void;
}

export function AuthorizedDevice({ device, currentDeviceId, onUserRemove }: AuthorizedDeviceProps) {
    const isOwnDevice = currentDeviceId && currentDeviceId === device.id;
    const [removeDialogProps, toggleRemoveDialog] = useDialog();

    // The naming of the BE payload is a bit confusing, we need to send the BE entity id for removal
    const handleUserRemove = () => device.id && onUserRemove(device.id);

    return (
        <>
            <Container padding={['200', '300']} width="100%">
                <Stack distribution="space-between">
                    <Stack vertical spacing="200">
                        <Typography textStyle="titleSmallStrong" as="div">
                            {getDeviceModelAndSystem(device)}
                        </Typography>
                        <Stack vertical spacing="50">
                            <Typography textStyle="bodyLarge" as="div">
                                {getDeviceLocation(device)}
                            </Typography>
                            <Typography textStyle="bodyLarge" variant="tertiary" as="div">
                                {getDeviceLastSignInTime(device)}
                            </Typography>
                        </Stack>
                    </Stack>

                    {!isOwnDevice && (
                        <Button variant="tertiary" type="button" size="small" onClick={toggleRemoveDialog}>
                            <LocalizedText textKey={TextKey.Remove} />
                        </Button>
                    )}
                    {isOwnDevice && (
                        <Tag variant="primary">
                            <LocalizedText textKey={TextKey.AuthorizedDeviceThisDevice} />
                        </Tag>
                    )}
                </Stack>
            </Container>
            <AuthorizedDeviceRemoveDialog {...removeDialogProps} onUserRemove={handleUserRemove} />
        </>
    );
}

function getDeviceModelAndSystem(device: UserDevice) {
    const { deviceModel, deviceSystem } = device;
    if (deviceModel && deviceSystem) {
        return `${deviceModel} (${deviceSystem})`;
    }

    return deviceModel || deviceSystem || UnicodeEntity.Dash;
}

function getDeviceLocation(device: UserDevice) {
    return device.location || UnicodeEntity.Dash;
}

function getDeviceLastSignInTime(device: UserDevice) {
    if (!device.lastSignInTime) {
        return UnicodeEntity.Dash;
    }

    return (
        <>
            <LocalizedLongDateFromUtc utcString={device.lastSignInTime} />
            {UnicodeEntity.Space}
            <LocalizedShortTimeFromUtc utcString={device.lastSignInTime} />
        </>
    );
}
